// Time helpers (plan 9.1.4): values are stored as UTC epoch ms; screens show the organisation time zone (Korea time).
// Ported from the mockup data.js (kst, dayStart, ymd, parseYmd, periodRange, delta) with "now" passed in instead of a
// fixed mock clock. Korea has no daylight saving time, so a fixed +9 h offset is exact.

export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;
/** Organisation display time zone. */
export const TIME_ZONE = 'Asia/Seoul';
export const TZ_OFFSET = 9 * HOUR;

export interface ZonedParts {
  y: number;
  m: number;
  d: number;
  h: number;
  mi: number;
  s: number;
  /** 0 = Sunday */
  wd: number;
}

const pad = (n: number) => (n < 10 ? '0' : '') + n;

export function kst(ts: number): ZonedParts {
  const d = new Date(ts + TZ_OFFSET);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds(), wd: d.getUTCDay() };
}
/** Start of the (Korea-time) day that contains ts. */
export const dayStart = (ts: number): number => Math.floor((ts + TZ_OFFSET) / DAY) * DAY - TZ_OFFSET;
export const kstDate = (y: number, m: number, d: number): number => Date.UTC(y, m - 1, d) - TZ_OFFSET;
/** 'YYYY-MM-DD' (Korea time). */
export function ymd(ts: number): string {
  const p = kst(ts);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}
export function parseYmd(str: string): number {
  const [y = 0, m = 1, d = 1] = String(str).split('-').map(Number);
  return kstDate(y, m, d);
}

export type PeriodKey = 'today' | '7d' | '30d' | '90d';
export interface CustomPeriod {
  from: string;
  to: string;
}
export type Period = PeriodKey | CustomPeriod;
export const PERIOD_KEYS: readonly PeriodKey[] = ['today', '7d', '30d', '90d'];
export const isCustomPeriod = (p: Period): p is CustomPeriod => typeof p === 'object' && p !== null;

export interface PeriodRange {
  from: number;
  to: number;
  prevFrom: number;
  prevTo: number;
  days: number;
  key: PeriodKey | 'custom';
}

/** Period → time range and the previous range of the same length (for "직전 7일보다 …" sentences). */
export function periodRange(period: Period, now: number = Date.now()): PeriodRange {
  const today = dayStart(now);
  let from: number;
  let to = now;
  let key: PeriodRange['key'] = isCustomPeriod(period) ? 'custom' : period;
  if (period === 'today') from = today;
  else if (period === '7d') from = today - 6 * DAY;
  else if (period === '30d') from = today - 29 * DAY;
  else if (period === '90d') from = today - 89 * DAY;
  else if (isCustomPeriod(period)) {
    from = parseYmd(period.from);
    to = Math.min(now, parseYmd(period.to || ymd(now)) + DAY);
  } else {
    from = today - 6 * DAY;
    key = '7d';
  }
  const len = to - from;
  let prevTo = from;
  const prevFrom = from - (period === 'today' ? DAY : Math.ceil(len / DAY) * DAY);
  if (period === 'today') prevTo = from - DAY + (now - today);
  return { from, to, prevFrom, prevTo, days: Math.max(1, Math.ceil(len / DAY)), key };
}

export type DeltaDir = 'up' | 'down' | 'flat' | 'new' | 'none';
export interface DeltaResult {
  dir: DeltaDir;
  pct: number | null;
}
/** Change between two values: rounded change under 1 % is "flat"; a confirmed 0 before is "new"; a missing value is "none". */
export function delta(cur: number | null | undefined, prev: number | null | undefined): DeltaResult {
  if (cur == null || prev == null) return { dir: 'none', pct: null };
  if (prev === 0) return cur === 0 ? { dir: 'flat', pct: 0 } : { dir: 'new', pct: null };
  const p = ((cur - prev) / prev) * 100;
  return { dir: Math.abs(p) < 1 ? 'flat' : p > 0 ? 'up' : 'down', pct: Math.round(Math.abs(p)) };
}

/** Number of calendar days in an inclusive 'YYYY-MM-DD' range. */
export const rangeDays = (from: string, to: string): number => Math.round((parseYmd(to) - parseYmd(from)) / DAY) + 1;
